import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { Badge, Box, Link, Typography } from '@mui/material';
import { useTheme } from '@parkdna/ui/theme';
import { NavLink } from 'react-router-dom';

import { useSupportRequests } from '@/features/support';
import { useSidebarStore } from '@/stores/sidebar';

const UNREAD_BADGE_MAX = 9;

/**
 * A link component that displays a Support entry pinned at the bottom of the
 * sidebar. Unlike `FAQLink`, this is an internal router link to `/support`.
 *
 * Features:
 * - Routes internally to `/support` via React Router `NavLink`
 * - Adapts to sidebar collapsed state (shows only the icon when collapsed)
 * - Shows an unread badge counting requests-with-unread (`unread_count > 0`),
 *   capped at "9+". When the sidebar is collapsed the badge is reduced to a dot.
 */
export const SupportLink = () => {
  const [theme] = useTheme();
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);
  const { data: requests } = useSupportRequests();

  const requestsWithUnreadCount =
    requests?.filter((request) => request.unread_count > 0).length ?? 0;
  const hasUnread = requestsWithUnreadCount > 0;

  return (
    <Box
      sx={{
        padding: isCollapsed ? '0 0.5rem' : '0 1rem',
        marginBottom: 2,
      }}
    >
      <Link
        component={NavLink}
        to="/support"
        underline="none"
        aria-label="Support"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
          padding: isCollapsed ? '0.5rem' : '0.75rem 1rem',
          borderRadius: `${theme.shape.borderRadius}px`,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.active': {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        <Badge
          color="error"
          overlap="circular"
          variant={isCollapsed ? 'dot' : 'standard'}
          badgeContent={isCollapsed ? undefined : requestsWithUnreadCount}
          max={UNREAD_BADGE_MAX}
          invisible={!hasUnread}
        >
          <SupportAgentOutlinedIcon
            sx={{
              fontSize: 20,
              color: theme.palette.text.secondary,
            }}
          />
        </Badge>
        {!isCollapsed && (
          <Typography variant="body2" sx={{ flex: 1 }}>
            Support
          </Typography>
        )}
      </Link>
    </Box>
  );
};
